import React from 'react';
import { Gift, CalendarClock } from 'lucide-react';
import { getPromotion } from '../../lib/promotions';

// Rewards expire at the end of a day in IST — format there, not in the
// browser's zone, so "31 Dec 2026" never shows as "1 Jan 2027".
export const validUntilLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T23:59:59+05:30` : iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric',
  });
};

/** "Valid till 31 Dec 2026" — shown on every reward message, in every state. */
const ValidityLine = ({ validUntil, what }) => {
  const until = validUntilLabel(validUntil || getPromotion()?.reward_valid_until);
  if (!until) return null;
  return (
    <p className="mt-1 flex items-center gap-1 font-semibold" data-testid="promo-validity">
      <CalendarClock className="w-3.5 h-3.5 shrink-0" />
      {what} valid till {until} — unused {what.toLowerCase()} expire after that.
    </p>
  );
};

/**
 * Sale reward notice for Day Pass purchases (buy X, get X free).
 *  - preview (default): what this purchase will earn, or a log-in prompt
 *  - granted: shown after payment, using the booking's own promo_reward
 */
export const PromoRewardNotice = ({ reward, granted = false, payingWithBalance = false }) => {
  if (!reward || !reward.freePasses) return null;
  const n = reward.freePasses;
  const passes = `${n} free Day Pass${n !== 1 ? 'es' : ''}`;
  const sale = reward.saleName ? `${reward.saleName}: ` : '';
  // Several dates bought together earn one reward for all their passes.
  const from = reward.multiDate ? 'the day after your last booked date' : 'the day after your paid pass';
  const across = reward.multiDate ? ' (counting the passes on all your dates)' : '';

  let body;
  if (!reward.eligible) {
    body = <>Log in or sign up before booking to get <strong>{passes}</strong> with this purchase. Guest bookings aren't eligible.</>;
  } else if (granted) {
    body = <><strong>{passes}</strong> {n !== 1 ? 'have' : 'has'} been added to your account — use {n !== 1 ? 'them' : 'it'} at any Clayworks centre from {from}.</>;
  } else if (payingWithBalance) {
    body = <>Pay by card to get <strong>{passes}</strong> with this purchase — the offer doesn't apply when paying with credits or Day Pass balance.</>;
  } else {
    body = <>You'll get <strong>{passes}</strong> with this purchase{across}, usable at any Clayworks centre from {from}. Added once payment is complete.</>;
  }

  return (
    <div
      className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300"
      data-testid={granted ? 'promo-reward-granted' : 'promo-reward-notice'}
    >
      <Gift className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <span>{sale}{body}</span>
        <ValidityLine validUntil={reward.validUntil} what="Free Day Passes" />
      </div>
    </div>
  );
};

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 1 }));
const hrs = (n) => `${fmt(n)} hr${Number(n) === 1 ? '' : 's'}`;

/**
 * Meeting Room 2+2 panel: paid booking value, bonus credits earned and total
 * credits value. `reward` comes from meetingRoomRewardPreview (estimate) or the
 * backend's promo_reward (exact). States: teaser (under 2 hrs), preview,
 * log-in prompt, paying-with-balance, and granted (after payment).
 */
export const MeetingRoomRewardPanel = ({ reward, eligible = true, granted = false, payingWithBalance = false }) => {
  if (!reward) return null;
  const sale = reward.saleName ? `${reward.saleName}: ` : '';

  if (reward.teaser) {
    return (
      <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300" data-testid="mr-offer-teaser">
        <Gift className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <span>{sale}book <strong>{reward.block} hours or more</strong> and get <strong>{reward.block} hours free</strong> as Meeting Room Credits.</span>
          <ValidityLine validUntil={reward.validUntil} what="Bonus credits" />
        </div>
      </div>
    );
  }

  let footer;
  if (!eligible) footer = 'Log in or sign up before booking to get these free hours. Guest bookings aren\'t eligible.';
  else if (granted) footer = `Added to your credit wallet — use them for meeting rooms at any Clayworks centre.`;
  else if (payingWithBalance) footer = 'Pay with money (card or UPI) to get these free hours — the offer doesn\'t apply when paying with credits.';
  else footer = `Credited to your wallet once payment is complete. Use them for meeting rooms at any Clayworks centre.`;

  return (
    <div className="mt-3 rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-xs" data-testid={granted ? 'mr-offer-granted' : 'mr-offer-panel'}>
      <div className="flex items-center gap-2 px-3 pt-2 font-semibold text-sm">
        <Gift className="w-4 h-4 shrink-0" />
        <span>{sale}+{hrs(reward.freeHours)} free as Meeting Room Credits</span>
      </div>
      <div className="px-3 py-2 space-y-1">
        <div className="flex justify-between gap-3" data-testid="mr-offer-paid">
          <span>Paid booking · {hrs(reward.paidHours)}</span>
          <span className="font-medium">
            {reward.paidAmount != null ? `₹${Number(reward.paidAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
            {reward.paidCredits ? ` · ${fmt(reward.paidCredits)} credits` : ''}
          </span>
        </div>
        <div className="flex justify-between gap-3 text-emerald-700 dark:text-emerald-400 font-medium" data-testid="mr-offer-bonus">
          <span>Bonus credits earned · {hrs(reward.freeHours)} free</span>
          <span>+{fmt(reward.bonusCredits)} credits</span>
        </div>
        <div className="flex justify-between gap-3 pt-1 border-t border-amber-300/60 dark:border-amber-800 font-semibold" data-testid="mr-offer-total">
          <span>Total credits value · {hrs(Number(reward.paidHours) + Number(reward.freeHours))}</span>
          <span>{fmt(reward.totalCredits)} credits</span>
        </div>
      </div>
      <div className="px-3 pb-2">
        <ValidityLine validUntil={reward.validUntil} what="Bonus credits" />
        <p className="text-[11px] opacity-80 mt-1">{footer}</p>
      </div>
    </div>
  );
};
